// Converted from shared/design/components/AlertToast.html by scripts/dc2tsx; edited by hand from here on.
import React from 'react';
import { DCLogic } from '../ds/DCLogic';
import { ip, route, sx } from '../ds/tpl';
import { currentLanguage, t } from '../i18n';
import { ago } from '../lib/format';

export interface AlertToastProps {
  sev?: 'crit' | 'warn' | (string & {});
  inline?: boolean;
  delay?: number;
  cycle?: boolean;
  demo?: any;
  /** live mode (shell): the alert shown, without cycling through samples */
  alert?: {
    account: string;
    cls: string;
    score?: number | null;
    detail: string;
    href: string;
    caseHref?: string;
  };
  /** fired time (ISO) for the "just now / 12s ago" label */
  firedAt?: string;
  onDismiss?: () => void;
  onSnooze?: () => void;
  onAction?: (e: any) => void;
  [key: string]: any;
}

// The user menu opens at the inline end of the top bar, so in RTL the toast keeps clear of its 260px
// column. Above the shell's 1100px breakpoint only: below it the bar wraps and the avatar moves.
const RTL_POS = 'inset-inline-end: calc(var(--space-6) + 260px + var(--space-4));';
const RTL_POS_MIN_WIDTH = 1100;

export class AlertToast extends DCLogic<AlertToastProps> {
  constructor(props) {
    super(props);
    this.state = { open: !(props.delay > 0), idx: 0, secs: 0 };
    this.items = props.items || [
      {
        account: '5033107',
        cls: 'Latency arbitrage',
        score: 93,
        detail: '+$5.8/lot markout at 1s on 14 fills in 10 min. Shares IP 91.108.4.77 with 5014382.',
      },
      {
        account: '5041288',
        cls: 'News trading',
        score: 88,
        detail: '9 positions opened within 2s of the US CPI release, 6.4 lots in total.',
      },
      {
        account: 'G-07',
        cls: 'Coordinated entry',
        score: 84,
        detail: '6 accounts opened NAS100 buys within 400ms from two datacenter IPs.',
      },
    ];
  }
  componentDidMount() {
    if ((this.props.delay ?? 0) > 0) this.t0 = setTimeout(() => this.setState({ open: true, secs: 0 }), this.props.delay);
    this.iv = setInterval(() => {
      this.setState({ secs: this.state.secs + 1 });
      if ((this.props.sev ?? 'crit') === 'warn' && this.state.open && this.state.secs >= 8 && !this.props.inline)
        this.setState({ open: false });
    }, 1000);
  }
  componentWillUnmount() {
    clearTimeout(this.t0);
    clearTimeout(this.t1);
    clearInterval(this.iv);
  }
  hide() {
    this.setState({ open: false });
    if (this.props.cycle ?? true) {
      clearTimeout(this.t1);
      this.t1 = setTimeout(() => this.setState({ open: true, idx: this.state.idx + 1, secs: 0 }), 9000);
    }
  }
  renderVals() {
    const p = this.props;
    const sev = p.sev ?? 'crit';
    const it = p.alert ? p.alert : Object.assign({}, this.items[this.state.idx % this.items.length], p.item || {});
    const crit = sev === 'crit';
    const inline = !!(p.inline ?? p.demo);
    const s = p.firedAt ? Math.max(0, Math.round((Date.now() - Date.parse(p.firedAt)) / 1000)) : this.state.secs;
    return {
      wrap: p.demo ? 'padding: 16px; background: var(--bg);' : '',
      show: this.state.open,
      wrapCls: inline ? '' : 'bx-toast-wrap',
      posStyle: inline ? 'max-width: 440px;' : currentLanguage().dir === 'rtl' && window.innerWidth > RTL_POS_MIN_WIDTH ? RTL_POS : '',
      toastCls: crit ? 'bx-toast' : 'bx-toast bx-toast--warn',
      dotColor: crit ? 'var(--crit)' : 'var(--warn)',
      dotAnim: crit ? 'bx-pulse var(--dur-pulse) infinite' : 'none',
      ebColor: crit ? 'var(--crit-text)' : 'var(--warn)',
      ringColor: crit ? 'var(--crit)' : 'var(--warn)',
      detailColor: crit ? 'var(--crit-text)' : 'var(--ink-2)',
      eyebrow:
        p.eyebrow ||
        (crit
          ? t('c.alerttoast.eyebrow-critical', 'CRITICAL · TOXIC ACCOUNT DETECTED')
          : t('c.alerttoast.eyebrow-warning', 'WARNING · FEED LAG')),
      ago: s < 5 ? t('c.alerttoast.just-now', 'just now') : ago(0, s * 1000),
      hasScore: it.score != null,
      score: it.score,
      account: it.account,
      cls: it.cls,
      detail: it.detail,
      queue: p.queue ?? (crit ? Math.max(0, this.items.length - 1 - (this.state.idx % this.items.length)) : 0),
      reviewHref: it.href || 'TRD-02_account-overview.html',
      caseHref: it.caseHref || 'CAS-02_case-detail.html',
      act: p.onAction,
      priCls: crit ? 'bx-btn bx-btn--sm bx-btn--crit' : 'bx-btn bx-btn--sm bx-btn--primary',
      secCls: crit ? 'bx-btn bx-btn--sm bx-btn--danger' : 'bx-btn bx-btn--sm',
      dismiss: () => (p.onDismiss ? p.onDismiss() : this.hide()),
      snooze: () => (p.onSnooze ? p.onSnooze() : this.hide()),
    };
  }
  view(v: any) {
    return (
      <div style={sx(`font-family: var(--font-sans); color: var(--ink); ${v.wrap ?? ''}`)}>
        {v.show ? (
          <div className={v.wrapCls} style={sx(`${v.posStyle ?? ''}`)}>
            <div className={v.toastCls} role="alert" aria-live="assertive">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: v.dotColor,
                    animation: v.dotAnim,
                  }}
                />
                <span
                  style={{
                    fontSize: '11px',
                    lineHeight: '14px',
                    fontWeight: '600',
                    letterSpacing: '.1em',
                    color: v.ebColor,
                  }}
                >
                  {ip(v.eyebrow)}
                </span>
                <span
                  className="bx-m"
                  style={{
                    marginInlineStart: 'auto',
                    fontSize: '11px',
                    color: v.ebColor,
                    opacity: '.8',
                  }}
                >
                  {ip(v.ago)}
                </span>
                <button
                  type="button"
                  aria-label={t('c.alerttoast.dismiss', 'Dismiss')}
                  onClick={v.dismiss}
                  style={{
                    width: '28px',
                    height: '28px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: '0',
                    borderRadius: 'var(--radius-md)',
                    color: v.ebColor,
                    cursor: 'pointer',
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {v.hasScore ? (
                  <div
                    className="bx-m"
                    style={{
                      width: '52px',
                      height: '52px',
                      flex: 'none',
                      borderRadius: '50%',
                      border: `3px solid ${v.ringColor ?? ''}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '17px',
                      fontWeight: '600',
                      color: v.ebColor,
                    }}
                  >
                    {ip(v.score)}
                  </div>
                ) : null}
                <div style={{ minWidth: '0' }}>
                  <div
                    style={{
                      fontSize: '15px',
                      lineHeight: '21px',
                      fontWeight: '600',
                      color: 'var(--ink)',
                    }}
                  >
                    <span className="bx-m">{ip(v.account)}</span> {'·'} {ip(v.cls)}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      lineHeight: '17px',
                      color: v.detailColor,
                    }}
                  >
                    {ip(v.detail)}
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <a href={route(v.reviewHref)} className={v.priCls} onClick={v.act}>
                  {t('c.alerttoast.review-now', 'Review now')}
                </a>
                <a href={route(v.caseHref)} className={v.secCls} onClick={v.act}>
                  {t('c.alerttoast.open-case', 'Open case')}
                </a>
                <button
                  type="button"
                  onClick={v.snooze}
                  style={{
                    minHeight: '30px',
                    background: 'transparent',
                    border: '0',
                    color: v.ebColor,
                    font: '12px var(--font-sans)',
                    cursor: 'pointer',
                  }}
                >
                  {t('c.alerttoast.snooze-15-min', 'Snooze 15 min')}
                </button>
                {v.queue ? (
                  <span
                    style={{
                      marginInlineStart: 'auto',
                      fontSize: '11px',
                      color: v.ebColor,
                      opacity: '.8',
                    }}
                  >
                    {t('c.alerttoast.n-more-in-queue', '+{n} more in queue', { n: v.queue })}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
